import { Component, Input } from '@angular/core';
import { PetService } from '../../../services/pet.service';
import { LabRecordModel } from '../../../models/lab-record.model';
import Swal from 'sweetalert2';
declare var $: any;

type LabValue = { val: string, unit: string, date: string, test_code: string };
type MatchedRow = { test_desc: string, values: { [lab: string]: LabValue } };
type UniqueEntry = { lab: string, records: LabRecordModel[] };
type HistoryEntry = { lab: string, records: LabRecordModel[] };

@Component({
  selector: 'ngx-lab-compare',
  templateUrl: './lab-compare.component.html',
  styleUrls: ['./lab-compare.component.scss']
})
export class LabCompareComponent {
  @Input() pet_no: number;

  private readonly LAB_COLORS = ['#007bff', '#fd7e14', '#28a745', '#e83e8c', '#6f42c1'];

  selected_labs: string[] = [];
  compared_labs: string[] = [];

  matched: MatchedRow[] = [];
  unique: UniqueEntry[] = [];

  chartTitle = '';
  chartOptions: any = null;
  chartHistoryByLab: HistoryEntry[] = [];

  constructor(public petService: PetService) { }

  open() {
    this.selected_labs = [];
    this.compared_labs = [];
    this.matched = [];
    this.unique = [];
    this.chartOptions = null;
    this.chartTitle = '';
    this.chartHistoryByLab = [];

    if (!this.petService.lab_test || this.petService.lab_test.length === 0) {
      this.petService.GetLabTestList().then(() => { });
    }

    $('#modalLabCompare').modal('show');
  }

  toggleLab(lab: string) {
    const idx = this.selected_labs.indexOf(lab);
    if (idx === -1) {
      this.selected_labs.push(lab);
    } else {
      this.selected_labs.splice(idx, 1);
    }
  }

  isLabSelected(lab: string): boolean {
    return this.selected_labs.includes(lab);
  }

  labColor(lab: string): string {
    const idx = this.compared_labs.indexOf(lab);
    return this.LAB_COLORS[(idx === -1 ? 0 : idx) % this.LAB_COLORS.length];
  }

  onCompare() {
    if (this.selected_labs.length < 2) {
      Swal.fire({ title: 'Select at least two lab sources to compare.', icon: 'warning' });
      return;
    }

    this.compared_labs = [...this.selected_labs];
    this.chartOptions = null;
    this.chartTitle = '';
    this.chartHistoryByLab = [];

    Swal.fire({
      title: 'Loading...',
      didOpen: () => {
        Swal.showLoading();
      },
    });

    Promise.all(
      this.compared_labs.map(lab => this.petService.getLabCompareRecords(this.pet_no, lab))
    ).then((recordsByLab: LabRecordModel[][]) => {
      this.buildComparison(this.compared_labs, recordsByLab);
      Swal.close();
    });
  }

  showChart(row: MatchedRow) {
    const labs = Object.keys(row.values);

    Swal.fire({
      title: 'Loading history...',
      didOpen: () => {
        Swal.showLoading();
      },
    });

    Promise.all(
      labs.map(lab => this.petService.getLabRecordHistory(this.pet_no, row.values[lab].test_code))
    ).then((historyByLab: LabRecordModel[][]) => {
      const byDateDesc = (a: LabRecordModel, b: LabRecordModel) => new Date(b.labtest_date).getTime() - new Date(a.labtest_date).getTime();
      this.chartHistoryByLab = labs.map((lab, i) => ({ lab, records: [...historyByLab[i]].sort(byDateDesc) }));
      this.buildChart(row.test_desc, labs, historyByLab);
      Swal.close();
    });
  }

  private buildComparison(labs: string[], recordsByLab: LabRecordModel[][]) {
    const key = (desc: string) => (desc || '').trim().toLowerCase();

    const groups = new Map<string, { test_desc: string, byLab: Map<string, LabRecordModel> }>();

    labs.forEach((lab, i) => {
      recordsByLab[i].forEach(record => {
        const k = key(record.test_desc);
        if (!groups.has(k)) {
          groups.set(k, { test_desc: record.test_desc, byLab: new Map<string, LabRecordModel>() });
        }
        groups.get(k).byLab.set(lab, record);
      });
    });

    const matched: MatchedRow[] = [];
    const uniqueByLab = new Map<string, LabRecordModel[]>(labs.map(lab => [lab, []] as [string, LabRecordModel[]]));

    groups.forEach(group => {
      if (group.byLab.size >= 2) {
        const values: { [lab: string]: LabValue } = {};
        group.byLab.forEach((record, lab) => {
          values[lab] = {
            val: record.labtest_val,
            unit: record.units,
            date: record.labtest_date,
            test_code: record.test_code
          };
        });
        matched.push({ test_desc: group.test_desc, values });
      } else {
        group.byLab.forEach((record, lab) => {
          uniqueByLab.get(lab).push(record);
        });
      }
    });

    this.matched = matched;
    this.unique = labs
      .map(lab => ({ lab, records: uniqueByLab.get(lab) }))
      .filter(entry => entry.records.length > 0);
  }

  private buildChart(test_desc: string, labs: string[], historyByLab: LabRecordModel[][]) {
    const timeKey = (dateStr: string) => new Date(dateStr).getTime().toString();

    const valuesByLab = labs.map((lab, i) => {
      const m = new Map<string, string>();
      historyByLab[i].forEach(r => m.set(timeKey(r.labtest_date), r.labtest_val));
      return m;
    });

    const allKeysSet = new Set<string>();
    valuesByLab.forEach(m => m.forEach((_, k) => allKeysSet.add(k)));
    const allKeys = Array.from(allKeysSet).sort((a, b) => Number(a) - Number(b));
    const xAxisData = allKeys.map(k => {
      const d = new Date(Number(k));
      return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    });

    this.chartTitle = test_desc;
    this.chartOptions = {
      legend: { data: labs, top: 0, left: 'center' },
      tooltip: { trigger: 'axis' },
      grid: { top: 40, bottom: 90, left: 40, right: 20, containLabel: true },
      xAxis: { data: xAxisData, silent: false, splitLine: { show: false }, axisLabel: { rotate: 30 } },
      yAxis: {},
      series: labs.map((lab, i) => ({
        name: lab,
        type: 'line',
        data: allKeys.map(k => valuesByLab[i].has(k) ? Number(valuesByLab[i].get(k)) : null),
        smooth: true,
        connectNulls: true,
        color: [this.labColor(lab)]
      })),
      animationEasing: 'elasticOut',
    };
  }
}
